import React from "react";
import { formatDate, formatMoney } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import { groupPurchaseOrderLines } from "@/lib/purchase-order-lines";
import { formatUnitQty } from "@apv-erp/status";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface PurchaseOrderPrintTemplateProps {
  po: any;
  config?: any;
}

export default function PurchaseOrderPrintTemplate({
  po,
  config,
}: PurchaseOrderPrintTemplateProps) {
  const c = getCompanyConfig(config);
  const now = new Date();
  // 1 dòng / vật tư khi đơn gom nhiều LSX (05/11/2026)
  const items: any[] = groupPurchaseOrderLines(Array.isArray(po.items) ? po.items : []);
  const totalAmount =
    Number(po.totalAmount) ||
    items.reduce(
      (s: number, it: any) =>
        s + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0),
      0
    );

  const poCode = po.poId || (po.id?.length > 20 ? `PO-${po.id.slice(0, 8)}` : po.id) || "PO";

  return (
    <div
      className="p-[10mm] text-black leading-snug text-[12px] bg-white w-full mx-auto min-h-[297mm]"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      {/* ========== COMPANY HEADER ========== */}
      <ISOPrintHeader
        config={config}
      />

      {/* ========== TITLE (CENTERED, NO QR) ========== */}
      <PrintDocumentTitle
        title="ĐƠN ĐẶT MUA HÀNG (PO)"
        subtitle="PURCHASE ORDER"
        docNo={poCode}
        date={po.orderDate || po.createdAt || now}
        dateLabel="Ngày đặt"
      />

      {/* ========== ORDER & VENDOR INFO (BORDERLESS 2-COL TABS) ========== */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[115px]"
          leftColumn={[
            {
              label: "Nhà cung cấp",
              value: po.vendor?.name || "—",
              isBold: true,
            },
            {
              label: "Mã NCC",
              value: po.vendor?.code || "—",
            },
            {
              label: "Địa chỉ NCC",
              value: po.vendor?.address || "—",
            },
            {
              label: "Mã số thuế",
              value: po.vendor?.taxCode || "—",
            },
            {
              label: "Người liên hệ",
              value: po.vendor?.contactName
                ? `${po.vendor.contactName} ${po.vendor?.phone ? `(${po.vendor.phone})` : ""}`
                : "—",
            },
            {
              label: "NV Mua hàng",
              value: po.creator?.fullName || po.user?.fullName || "Phòng Thu Mua APV",
            },
          ]}
          rightColumn={[
            {
              label: "Số PO",
              value: poCode,
              isBold: true,
              highlight: true,
            },
            {
              label: "Ngày đặt",
              value: formatDate(po.orderDate || po.createdAt || new Date()),
            },
            {
              label: "Hạn giao hàng",
              value: po.expectedDate ? formatDate(po.expectedDate) : "Theo tiến độ sản xuất",
              isBold: true,
            },
            {
              label: "Nơi giao hàng",
              value: `${c.companyName} — ${po.deliveryAddress || c.address2 || c.address1}`,
            },
            {
              label: "Thanh toán",
              value: po.paymentTerms || "Theo thỏa thuận hợp đồng",
            },
          ]}
        />
      </div>

      {/* ========== ITEMS TABLE ========== */}
      <table className="w-full border-collapse border border-black mb-2 text-[11px] table-fixed">
        <thead>
          <tr className="bg-slate-100 font-bold text-center">
            <th className="border border-black px-1 py-1.5" style={{ width: "5%" }}>
              STT<br /><i className="font-normal">No.</i>
            </th>
            <th className="border border-black px-1.5 py-1.5 text-left" style={{ width: "15%" }}>
              Mã vật tư<br /><i className="font-normal">Item Code</i>
            </th>
            <th className="border border-black px-1.5 py-1.5 text-left" style={{ width: "35%" }}>
              Tên & Quy cách vật tư<br /><i className="font-normal">Description & Specs</i>
            </th>
            <th className="border border-black px-1 py-1.5" style={{ width: "8%" }}>
              ĐVT<br /><i className="font-normal">Unit</i>
            </th>
            <th className="border border-black px-1 py-1.5" style={{ width: "10%" }}>
              SL<br /><i className="font-normal">Q'ty</i>
            </th>
            <th className="border border-black px-1.5 py-1.5 text-right" style={{ width: "12%" }}>
              Đơn giá<br /><i className="font-normal">Price (₫)</i>
            </th>
            <th className="border border-black px-1.5 py-1.5 text-right" style={{ width: "15%" }}>
              Thành tiền<br /><i className="font-normal">Amount (₫)</i>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item: any, index: number) => {
              const qty = Number(item.quantity) || 0;
              const price = Number(item.unitPrice) || 0;
              const amt = Number(item.amount) || qty * price;
              const matCode =
                item.material?.code ||
                item.material?.materialId ||
                item.materialCode ||
                "—";
              const matName =
                item.material?.name ||
                item.materialName ||
                "Vật tư sản xuất";
              const specs =
                item.material?.specs ||
                item.specs ||
                "";

              return (
                <tr key={index} className="break-inside-avoid">
                  <td className="border border-black px-1 py-1.5 text-center">
                    {index + 1}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 font-mono text-slate-700">
                    {matCode}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 text-left">
                    <div className="font-bold text-slate-900">{matName}</div>
                    {specs && (
                      <div className="text-[10px] text-slate-600 italic">
                        {specs}
                      </div>
                    )}
                    {/* Ghi chú dòng in riêng, không chỉ khi vật tư không có quy cách */}
                    {item.notes && (
                      <div className="text-[10px] text-slate-800 whitespace-pre-wrap" data-testid="po-print-line-note">
                        Ghi chú: {item.notes}
                      </div>
                    )}
                  </td>
                  {/* Đặt theo ĐVT nhập (Ram / Tấn) thì in số đã đặt với NCC + quy đổi ĐVT chính (05/11/2026) */}
                  <td className="border border-black px-1 py-1.5 text-center whitespace-nowrap">
                    {item.entryUnit && item.entryQty != null ? item.entryUnit : item.material?.unit || item.unit || "—"}
                  </td>
                  <td className="border border-black px-1 py-1.5 text-center font-bold font-mono whitespace-nowrap" data-testid="po-print-qty">
                    {item.entryUnit && item.entryQty != null ? (
                      <>
                        {formatUnitQty(Number(item.entryQty))}
                        <div className="text-[9px] font-normal text-slate-600">= {formatUnitQty(qty)} {item.material?.unit || ""}</div>
                      </>
                    ) : (
                      formatUnitQty(qty)
                    )}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 text-right tabular-nums whitespace-nowrap">
                    {item.entryUnit && item.entryUnitPrice != null
                      ? formatMoney(Number(item.entryUnitPrice))
                      : price > 0
                        ? formatMoney(price)
                        : "—"}
                  </td>
                  <td className="border border-black px-1.5 py-1.5 text-right font-bold tabular-nums whitespace-nowrap">
                    {amt > 0 ? formatMoney(amt) : "—"}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={7}
                className="border border-black px-2 py-4 text-center text-slate-500 italic"
              >
                Chưa có chi tiết vật tư nào
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="bg-yellow-50 font-bold">
            <td
              colSpan={5}
              className="border border-black px-2 py-2 text-left text-[12px]"
            >
              TỔNG GIÁ TRỊ ĐƠN HÀNG/ <i>TOTAL PURCHASE AMOUNT</i>:
            </td>
            <td
              colSpan={2}
              className="border border-black px-2 py-2 text-right text-[13px] text-red-700 tabular-nums"
            >
              {formatMoney(totalAmount)}
            </td>
          </tr>
          <tr>
            <td
              colSpan={7}
              className="border border-black px-2 py-1.5 text-[11px] italic"
            >
              <b>Số tiền bằng chữ/ <i>In words</i>:</b>{" "}
              {numberToVietnameseWords(totalAmount, "đồng")}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* ========== TERMS & NOTES ========== */}
      <div className="grid grid-cols-2 gap-4 text-[11px] mt-4 mb-6">
        <div>
          <h3 className="font-bold mb-1 border-b border-black pb-0.5 inline-block">
            Điều khoản giao nhận & Chất lượng:
          </h3>
          <ul className="list-disc pl-4 space-y-0.5 text-[10px] leading-snug">
            <li>Vật tư mới 100%, đúng quy chuẩn kỹ thuật và mẫu đã duyệt.</li>
            <li>Kèm theo phiếu xuất xưởng và kết quả kiểm nghiệm CO/CQ.</li>
            <li>Hóa đơn tài chính VAT hợp lệ gửi cùng biên bản giao hàng.</li>
            {po.notes && (
              <li className="font-bold text-red-700">
                Ghi chú riêng: {po.notes}
              </li>
            )}
          </ul>
        </div>
        <div>
          <h3 className="font-bold mb-1 border-b border-black pb-0.5 inline-block">
            Địa điểm nhận hàng & Liên hệ kho APV:
          </h3>
          <div className="text-[10px] space-y-0.5 leading-snug">
            <p><b>Địa chỉ kho:</b> {c.address2 || c.address1}</p>
            <p><b>Hotline tiếp nhận:</b> {c.phone}</p>
            <p><b>Email xác nhận:</b> {c.email}</p>
          </div>
        </div>
      </div>

      {/* ========== 4-PARTY FOOTER SIGNATURES ========== */}
      <div className="grid grid-cols-4 gap-2 mt-8 text-[11px] text-center break-inside-avoid">
        <div>
          <div className="font-bold mb-1">NGƯỜI LẬP ĐƠN</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Ký & ghi rõ họ tên)
          </div>
          <div className="font-medium text-slate-800">
            {po.creator?.fullName || "...................."}
          </div>
        </div>
        <div>
          <div className="font-bold mb-1">TRƯỞNG PHÒNG THU MUA</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Ký & ghi rõ họ tên)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
        <div>
          <div className="font-bold mb-1">BAN GIÁM ĐỐC</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Duyệt & đóng dấu)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
        <div>
          <div className="font-bold mb-1">ĐẠI DIỆN NHÀ CUNG CẤP</div>
          <div className="text-slate-500 italic text-[10px] mb-14">
            (Ký nhận & xác nhận PO)
          </div>
          <div className="font-medium text-slate-800">
            {po.vendor?.name ? po.vendor.name.slice(0, 24) : "...................."}
          </div>
        </div>
      </div>
    </div>
  );
}
